/**
 * Encoder/decoder for the SimSnapshot.entities binary container
 * (layout: contracts/render.ts + contracts/field.ts). Lane 2 reads these
 * bytes; sim systems fill them via optional engine hooks.
 */
import {
  SNAPSHOT_ENTITIES_MAGIC, SECTION_POSE, SECTION_FILD, SECTION_UIST,
  type RenderPose,
} from '../../contracts/render';
import type { FieldSlice } from '../../contracts/field';
import { BinWriter, BinReader } from './serialize';
import { writePayload, readPayload } from './engine';

/** FNV-1a 32-bit string hash - the wire identity for ids (Math-free). */
export function idHash(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Number((BigInt(h) * 0x01000193n) & 0xffffffffn);
  }
  return h >>> 0;
}

const TRAP_STATE_CODE: Record<FieldSlice['traps'][number]['state'], number> = {
  armed: 0, sprung_empty: 1, holding: 2,
};

function writePosePayload(w: BinWriter, poses: readonly RenderPose[]): void {
  w.u32(poses.length);
  for (const p of poses) {
    w.u32(idHash(p.id));
    w.u16(p.speciesIdx);
    w.u8(p.gait);
    w.u8(0);
    w.i64(p.x);
    w.i64(p.z);
    w.u16(p.heading);
    w.u16(p.scale);
    w.u32(p.speed >>> 0);
  }
}

function writeFieldPayload(w: BinWriter, f: FieldSlice | null): void {
  if (!f) {
    w.i64(0).i64(0).u32(0).u8(0).u8(0); // playerX, playerZ, heading, crouched, pad
    w.u32(0).u32(0).u32(0);             // spotted, trackable, traps: all empty
    return;
  }
  w.i64(f.playerX).i64(f.playerZ).u32(f.heading & 0xffff).u8(f.crouched ? 1 : 0).u8(0);
  w.u32(f.spottedAnimalIds.length);
  for (const id of f.spottedAnimalIds) w.u32(idHash(id));
  w.u32(f.trackableAnimalIds.length);
  for (const id of f.trackableAnimalIds) w.u32(idHash(id));
  w.u32(f.traps.length);
  for (const t of f.traps) {
    w.u32(idHash(t.id));
    w.u32(0); // trapTypeIdx: Mgmt-Sim trap-table index; 0 until that data lands
    w.u8(TRAP_STATE_CODE[t.state]);
    w.u8(0);
    w.i64(t.x);
    w.i64(t.z);
    w.u32(t.holdingAnimalId === null ? 0 : idHash(t.holdingAnimalId));
  }
}

export interface SnapshotSections {
  poses: readonly RenderPose[];
  field: FieldSlice | null;
  ui: Record<string, unknown>;
}

function writeSection(w: BinWriter, fourcc: number, payload: Uint8Array): void {
  w.u32(fourcc);
  w.u32(payload.length);
  for (const b of payload) w.u8(b);
  const pad = (4 - (payload.length % 4)) % 4;
  for (let i = 0; i < pad; i++) w.u8(0);
}

/** Encode the full entities buffer (header + sections in ascending fourcc). */
export function encodeEntityBuffer(sections: SnapshotSections): Uint8Array {
  const w = new BinWriter();
  for (const ch of SNAPSHOT_ENTITIES_MAGIC) w.u8(ch.charCodeAt(0));
  w.u32(3); // sectionCount
  const fild = new BinWriter();
  writeFieldPayload(fild, sections.field);
  writeSection(w, SECTION_FILD, fild.finish()); // ascending fourcc order
  const pose = new BinWriter();
  writePosePayload(pose, sections.poses);
  writeSection(w, SECTION_POSE, pose.finish());
  const uist = new BinWriter();
  writePayload(uist, sections.ui);
  writeSection(w, SECTION_UIST, uist.finish());
  return w.finish();
}

export interface DecodedPose {
  idHash: number; speciesIdx: number; gait: number;
  x: number; z: number; heading: number; scale: number; speed: number;
}

export interface DecodedEntityBuffer {
  poseCount: number;
  poses: DecodedPose[];
  fieldPresent: boolean;
  fieldPlayer?: { x: number; z: number; heading: number; crouched: boolean };
  fieldTraps?: Array<{ idHash: number; typeIdx: number; state: number; x: number; z: number; holdingAnimalIdHash: number }>;
  spotted: number[];
  trackable: number[];
  trapCount: number;
  ui: Record<string, unknown>;
}

/** Reader for tests and for Lane 2's verification harness. */
export function decodeEntityBuffer(data: Uint8Array): DecodedEntityBuffer {
  const r = new BinReader(data);
  let magic = '';
  for (let i = 0; i < 4; i++) magic += String.fromCharCode(r.u8());
  if (magic !== SNAPSHOT_ENTITIES_MAGIC) throw new Error(`snapshot: bad magic '${magic}'`);
  const sectionCount = r.u32();
  const out: DecodedEntityBuffer = {
    poseCount: 0, poses: [], fieldPresent: false, spotted: [], trackable: [], trapCount: 0, ui: {},
  };
  for (let s = 0; s < sectionCount; s++) {
    const fourcc = r.u32();
    const len = r.u32();
    const payload = new BinReader(r.rawBytes(len));
    if (fourcc === SECTION_POSE) {
      const count = payload.u32();
      out.poseCount = count;
      for (let i = 0; i < count; i++) {
        const idHashV = payload.u32();
        const speciesIdx = payload.u16();
        const gait = payload.u8();
        payload.u8(); // pad
        const x = payload.i64();
        const z = payload.i64();
        const heading = payload.u16();
        const scale = payload.u16();
        const speed = payload.u32();
        out.poses.push({ idHash: idHashV, speciesIdx, gait, x, z, heading, scale, speed });
      }
    } else if (fourcc === SECTION_FILD) {
      const x = payload.i64(), z = payload.i64(), heading = payload.u32();
      const crouched = payload.u8() !== 0; payload.u8();
      out.fieldPlayer = { x, z, heading, crouched };
      out.fieldTraps = [];
      const spottedCount = payload.u32();
      for (let i = 0; i < spottedCount; i++) out.spotted.push(payload.u32());
      const trackableCount = payload.u32();
      for (let i = 0; i < trackableCount; i++) out.trackable.push(payload.u32());
      out.trapCount = payload.u32();
      for (let i = 0; i < out.trapCount; i++) {
        const idHash = payload.u32(), typeIdx = payload.u32(), state = payload.u8(); payload.u8();
        const x = payload.i64(), z = payload.i64(), holdingAnimalIdHash = payload.u32();
        out.fieldTraps.push({ idHash, typeIdx, state, x, z, holdingAnimalIdHash });
      }
      out.fieldPresent = true;
    } else if (fourcc === SECTION_UIST) {
      out.ui = readPayload(payload) as Record<string, unknown>;
    } else {
      throw new Error(`snapshot: unknown section ${String(fourcc)}`);
    }
    const pad = (4 - (len % 4)) % 4;
    for (let i = 0; i < pad; i++) r.u8();
  }
  return out;
}
